import type { TCompanyRelation } from "types/authorization";

import { Homepage } from "components/pages/Homepage";
import { Route, type TShouldRenderFunction } from "routes/route";
import { lazyImportWithRetry as lazy } from "utils/retryDynamicImport/retryDynamicImport";

const AccessReportPage = lazy(
  () => import("components/pages/AccessReportPage"),
  (module) => ({ default: module.AccessReportPage }),
);
const AccessReviewDashboardPage = lazy(
  () => import("components/pages/AccessReviewDashboardPage"),
  (module) => ({ default: module.AccessReviewDashboardPage }),
);
const AccessReviewTemplatePage = lazy(
  () => import("components/pages/AccessReviewTemplatePage"),
  (module) => ({ default: module.AccessReviewTemplatePage }),
);
const AccessReviewTemplatesPage = lazy(
  () => import("components/pages/AccessReviewTemplatesPage"),
  (module) => ({ default: module.AccessReviewTemplatesPage }),
);
const AdminAccessReviewPage = lazy(
  () => import("components/pages/AdminAccessReviewPage"),
  (module) => ({ default: module.AdminAccessReviewPage }),
);
const AdminResourceAccessReviewPage = lazy(
  () => import("components/pages/AdminResourceAccessReview"),
  (module) => ({
    default: module.AdminResourceAccessReviewPage,
  }),
);
const AdminSubordinateAccessReviewPage = lazy(
  () => import("components/pages/AdminSubordinateAccessReview"),
  (module) => ({
    default: module.AdminSubordinateAccessReviewPage,
  }),
);
const AuditLogsPage = lazy(
  () => import("components/pages/AuditLogsPage"),
  (module) => ({ default: module.AuditLogsPage }),
);
const BulkActionsPage = lazy(
  () => import("components/pages/BulkActionsPage"),
  (module) => ({ default: module.BulkActionsPage }),
);
const BundlesPage = lazy(
  () => import("components/pages/BundlesPage"),
  (module) => ({ default: module.BundlesPage }),
);
const CreateIntegrationPage = lazy(
  () => import("components/pages/CreateIntegrationPage"),
  (module) => ({ default: module.CreateIntegrationPage }),
);
const CreateRulePage = lazy(
  () => import("components/pages/CreateRulePage"),
  (module) => ({ default: module.CreateRulePage }),
);
const EditRulePage = lazy(
  () => import("components/pages/CreateRulePage/EditRulePage"),
  (module) => ({ default: module.EditRulePage }),
);
const PermissionsPage = lazy(
  () => import("components/pages/PermissionsPage"),
  (module) => ({ default: module.PermissionsPage }),
);
const IntegrationPage = lazy(
  () => import("components/pages/IntegrationPage"),
  (module) => ({ default: module.IntegrationPage }),
);
const IntegrationResourcePage = lazy(
  () => import("components/pages/IntegrationResourcePage"),
  (module) => ({ default: module.IntegrationResourcePage }),
);
const IntegrationResourceRolePage = lazy(
  () => import("components/pages/IntegrationResourceRolePage"),
  (module) => ({
    default: module.IntegrationResourceRolePage,
  }),
);
const IntegrationsPage = lazy(
  () => import("components/pages/IntegrationsPage"),
  (module) => ({ default: module.IntegrationsPage }),
);
const JitAnalysisPage = lazy(
  () => import("components/pages/JitAnalysisPage"),
  (module) => ({ default: module.JitAnalysisPage }),
);
const MyPermissionsPage = lazy(
  () => import("components/pages/MyPermissionsPage"),
  (module) => ({ default: module.MyPermissionsPage }),
);
const NewRequestPage = lazy(
  () => import("components/pages/NewRequestPage"),
  (module) => ({ default: module.NewRequestPage }),
);
const PastRequestsPage = lazy(
  () => import("components/pages/PastRequestsPage"),
  (module) => ({ default: module.PastRequestsPage }),
);
const RequestsPage = lazy(
  () => import("components/pages/RequestsPage"),
  (module) => ({ default: module.RequestsPage }),
);
const PoliciesPage = lazy(
  () => import("components/pages/PoliciesPage"),
  (module) => ({ default: module.PoliciesPage }),
);
const ProfilePage = lazy(
  () => import("components/pages/ProfilePage"),
  (module) => ({ default: module.ProfilePage }),
);
const RequestsLogPage = lazy(
  () => import("components/pages/RequestsLogPage"),
  (module) => ({ default: module.RequestsLogPage }),
);
const ReportingPage = lazy(
  () => import("components/pages/ReportingPage"),
  (module) => ({ default: module.ReportingPage }),
);
const ResourceAccessReportPage = lazy(
  () => import("components/pages/ResourceAccessReportPage"),
  (module) => ({ default: module.ResourceAccessReportPage }),
);
const RulesPage = lazy(
  () => import("components/pages/RulesPage"),
  (module) => ({ default: module.RulesPage }),
);
const SettingsPage = lazy(
  () => import("components/pages/SettingsPage"),
  (module) => ({ default: module.SettingsPage }),
);
const DirectoryConfigurationPage = lazy(
  () => import("components/pages/DirectoryConfigurationPage"),
  (module) => ({ default: module.DirectoryConfigurationPage }),
);
const SubordinateAccessReportPage = lazy(
  () => import("components/pages/SubordinateAccessReportPage"),
  (module) => ({
    default: module.SubordinateAccessReportPage,
  }),
);
const TasksPage = lazy(
  () => import("components/pages/TasksPage"),
  (module) => ({ default: module.TasksPage }),
);
const UserPage = lazy(
  () => import("components/pages/UserPage"),
  (module) => ({ default: module.UserPage }),
);
const UsersPage = lazy(
  () => import("components/pages/UsersPage"),
  (module) => ({ default: module.UsersPage }),
);
const WorkflowsPage = lazy(
  () => import("components/pages/WorkflowsPage"),
  (module) => ({ default: module.WorkflowsPage }),
);
const CreateWorkflowPage = lazy(
  () => import("components/pages/WorkflowsPage/components/EditWorkflow"),
  (module) => ({
    default: module.CreateWorkflowPage,
  }),
);
const EditWorkflowPage = lazy(
  () => import("components/pages/WorkflowsPage/components/EditWorkflow"),
  (module) => ({
    default: module.EditWorkflowPage,
  }),
);
const WorkflowsWebhooksPage = lazy(
  () => import("components/pages/WorkflowsWebhooksPage"),
  (module) => ({ default: module.WorkflowsWebhooksPage }),
);

export type TRoutes = { [key: string]: Route };

const fgaOrAdmin = (relation: TCompanyRelation): TShouldRenderFunction =>
  ({ flags, hasPermission, user }) =>
    flags?.enableOpenFgaAuthorization ? Boolean(hasPermission?.(relation)) : user.isAdmin;

const shouldRenderAccessReview: TShouldRenderFunction = (opts) =>
  fgaOrAdmin("can_view_access_review_page")(opts) && !!opts.company?.enableAccessReviews;

const shouldRenderAccessReviewTemplateCreate: TShouldRenderFunction = (opts) =>
  fgaOrAdmin("can_view_access_review_template_create_page")(opts) && !!opts.company?.enableAccessReviews;

const shouldRenderAccessReviewTemplateEdit: TShouldRenderFunction = (opts) =>
  fgaOrAdmin("can_view_access_review_template_edit_page")(opts) && !!opts.company?.enableAccessReviews;

const shouldRenderAccessReport: TShouldRenderFunction = ({ company, flags, hasPermission, user }) =>
  user.hasAccessReviewReports
  && (!flags?.enableOpenFgaAuthorization || Boolean(hasPermission?.("can_view_access_report_page")))
  && !!company?.enableAccessReviews;

export const routes = {
  accessReport: new Route({
    animationOrder: 8,
    matches: ["/accessReport", "/accessReport/:accessReviewId"],
    Page: AccessReportPage,
    pageId: "AccessReport",
    shouldRender: shouldRenderAccessReport,
    subRoutes: {
      resourceReview: new Route({
        animationOrder: 2,
        matches: ["/resources/:resourceId"],
        Page: ResourceAccessReportPage,
        pageId: "ResourceAccessReport",
        shouldRender: shouldRenderAccessReport,
      }),
      subordinateReview: new Route({
        animationOrder: 1,
        matches: ["/subordinates/:subordinateId"],
        Page: SubordinateAccessReportPage,
        pageId: "SubordinateAccessReport",
        shouldRender: shouldRenderAccessReport,
      }),
    },
  }),
  accessReview: new Route({
    animationOrder: 7,
    matches: ["/accessReview"],
    Page: AccessReviewDashboardPage,
    pageId: "AccessReview",
    shouldRender: shouldRenderAccessReview,
    subRoutes: {
      adminResourceReview: new Route({
        animationOrder: 2,
        matches: ["/resources/:resourceId"],
        Page: AdminResourceAccessReviewPage,
        pageId: "AdminResourceAccessReview",
        shouldRender: shouldRenderAccessReview,
      }),
      adminSubordinateReview: new Route({
        animationOrder: 3,
        matches: ["/subordinates/:subordinateId"],
        Page: AdminSubordinateAccessReviewPage,
        pageId: "AdminSubordinateAccessReview",
        shouldRender: shouldRenderAccessReview,
      }),
      report: new Route({
        animationOrder: 1,
        matches: ["/reports/:reportId"],
        Page: AdminAccessReviewPage,
        pageId: "AccessReviewReport",
        shouldRender: shouldRenderAccessReview,
      }),
      template: new Route({
        animationOrder: 4,
        matches: ["/templates"],
        Page: AccessReviewTemplatesPage,
        pageId: "AccessReviewTemplatesPage",
        shouldRender: shouldRenderAccessReview,
        subRoutes: {
          create: new Route({
            animationOrder: 1,
            matches: ["/create"],
            Page: AccessReviewTemplatePage,
            pageId: "AccessReviewTemplatePage",
            shouldRender: shouldRenderAccessReviewTemplateCreate,
          }),
          edit: new Route({
            animationOrder: 2,
            matches: ["/edit/:templateId"],
            Page: AccessReviewTemplatePage,
            pageId: "AccessReviewTemplatePage",
            shouldRender: shouldRenderAccessReviewTemplateEdit,
          }),
        },
      }),
    },
  }),
  auditLogs: new Route({
    animationOrder: 10,
    matches: ["/auditLogs"],
    Page: AuditLogsPage,
    pageId: "AuditLogs",
    shouldRender: fgaOrAdmin("can_view_audit_logs_page"),
  }),
  bulkActions: new Route({
    matches: ["/bulkActions"],
    Page: BulkActionsPage,
    pageId: "BulkActions",
    shouldRender: fgaOrAdmin("can_view_bulk_actions_page"),
  }),
  bundles: new Route({
    animationOrder: 4,
    matches: ["/bundles"],
    Page: BundlesPage,
    pageId: "Bundles",
    shouldRender: fgaOrAdmin("can_view_bundles_page"),
  }),
  home: new Route({
    inAnimation: {
      name: "fade-move-from-center",
    },
    matches: ["/"],
    outAnimation: {
      name: "fade-move-from-center",
      reverse: true,
    },
    Page: Homepage,
    pageId: "Home",
  }),
  identityGraph: new Route({
    animationOrder: 1,
    matches: ["/permissions", "/graph"],
    Page: PermissionsPage,
    pageId: "Permissions",
    shouldRender: fgaOrAdmin("can_view_permissions_page"),
  }),
  integrations: new Route({
    animationOrder: 3,
    matches: ["/integrations"],
    Page: IntegrationsPage,
    pageId: "Integrations",
    shouldRender: fgaOrAdmin("can_view_integrations_page"),
    subRoutes: {
      createIntegration: new Route({
        animationOrder: 1,
        matches: ["/create"],
        Page: CreateIntegrationPage,
        pageId: "CreateIntegration",
        shouldRender: fgaOrAdmin("can_view_integrations_page"),
      }),
      integration: new Route({
        animationOrder: 3,
        matches: ["/:integrationId"],
        Page: IntegrationPage,
        pageId: "Integration",
        shouldRender: fgaOrAdmin("can_view_integrations_page"),
        subRoutes: {
          integrationResource: new Route({
            animationOrder: 2,
            matches: ["/resources/:integrationResourceId"],
            Page: IntegrationResourcePage,
            pageId: "IntegrationResource",
            shouldRender: fgaOrAdmin("can_view_integrations_page"),
            subRoutes: {
              integrationResourceRole: new Route({
                animationOrder: 1,
                matches: ["/roles/:integrationResourceRoleId"],
                Page: IntegrationResourceRolePage,
                pageId: "IntegrationResourceRole",
                shouldRender: fgaOrAdmin("can_view_integrations_page"),
              }),
            },
          }),
        },
      }),
    },
  }),
  newRequest: new Route({
    hideSideNav: true,
    inAnimation: {
      name: "fade-move-from-center",
    },
    matches: ["/newRequest"],
    outAnimation: {
      name: "fade-move-from-center",
      reverse: true,
    },
    Page: NewRequestPage,
    pageId: "NewRequest",
  }),
  policies: new Route({
    animationOrder: 5,
    matches: ["/policies"],
    Page: PoliciesPage,
    pageId: "Policies",
    shouldRender: fgaOrAdmin("can_view_policies_page"),
  }),
  profile: new Route({
    animationOrder: 13,
    inAnimation: {
      name: "fade-move-from-center",
    },
    matches: ["/profile"],
    outAnimation: {
      name: "fade-move-from-center",
      reverse: true,
    },
    Page: ProfilePage,
    pageId: "Profile",
  }),
  reporting: new Route({
    animationOrder: 11,
    matches: ["/reporting"],
    Page: ReportingPage,
    pageId: "Reporting",
    shouldRender: (opts) => fgaOrAdmin("can_view_reporting_page")(opts) && !!opts.flags?.enableReporting,
  }),
  requests: new Route({
    matches: ["/requests"],
    Page: RequestsPage,
    pageId: "Requests",
    subRoutes: {
      myPermissions: new Route({
        matches: ["/myPermissions"],
        Page: MyPermissionsPage,
        pageId: "MyPermissions",
      }),
      past: new Route({
        matches: ["/past"],
        Page: PastRequestsPage,
        pageId: "PastRequests",
      }),
    },
  }),
  requestsLog: new Route({
    animationOrder: 9,
    matches: ["/requestHistory"],
    Page: RequestsLogPage,
    pageId: "RequestsLog",
    shouldRender: fgaOrAdmin("can_view_requests_log_page"),
  }),
  rules: new Route({
    animationOrder: 50,
    matches: ["/rules"],
    Page: RulesPage,
    pageId: "Rules",
    shouldRender: fgaOrAdmin("can_view_rules_page"),
    subRoutes: {
      createRule: new Route({
        animationOrder: 1,
        hideSideNav: true,
        matches: ["/create"],
        Page: CreateRulePage,
        pageId: "RulesCreate",
        shouldRender: fgaOrAdmin("can_view_rules_page"),
      }),
      editRule: new Route({
        animationOrder: 1,
        hideSideNav: true,
        matches: ["/edit/:ruleId"],
        Page: EditRulePage,
        pageId: "RulesEdit",
        shouldRender: fgaOrAdmin("can_view_rules_page"),
      }),
    },
  }),
  settings: new Route({
    animationOrder: 11,
    matches: ["/organizationSettings"],
    Page: SettingsPage,
    pageId: "Settings",
    shouldRender: fgaOrAdmin("can_view_settings_page"),
    subRoutes: {
      directoryConfiguration: new Route({
        animationOrder: 1,
        hideSideNav: true,
        inAnimation: {
          name: "fade-move-from-center",
        },
        matches: ["/directoryConfiguration/:idpType/:idpId?"],
        outAnimation: {
          name: "fade-move-from-center",
          reverse: true,
        },
        Page: DirectoryConfigurationPage,
        pageId: "DirectoryConfigurationPage",
        shouldRender: fgaOrAdmin("can_view_settings_page"),
      }),
    },
  }),
  tasks: new Route({
    animationOrder: 6,
    matches: ["/tasks"],
    Page: TasksPage,
    pageId: "Tasks",
  }),
  users: new Route({
    animationOrder: 12,
    matches: ["/users"],
    Page: UsersPage,
    pageId: "Users",
    shouldRender: fgaOrAdmin("can_view_users_page"),
    subRoutes: {
      jitAnalysis: new Route({
        matches: ["/jitAnalysis"],
        Page: JitAnalysisPage,
        pageId: "JitAnalysis",
        shouldRender: fgaOrAdmin("can_view_jit_analysis_page"),
      }),
      user: new Route({
        matches: ["/:userId"],
        Page: UserPage,
        pageId: "User",
        shouldRender: fgaOrAdmin("can_view_users_page"),
      }),
    },
  }),
  workflows: new Route({
    animationOrder: 2,
    matches: ["/workflows"],
    Page: WorkflowsPage,
    pageId: "Workflows",
    shouldRender: fgaOrAdmin("can_view_workflows_page"),
    subRoutes: {
      createWorkflow: new Route({
        hideSideNav: true,
        matches: ["/create"],
        Page: CreateWorkflowPage,
        pageId: "CreateWorkflow",
        shouldRender: fgaOrAdmin("can_view_workflows_page"),
      }),
      editWorkflow: new Route({
        hideSideNav: true,
        matches: ["/edit/:workflowId"],
        Page: EditWorkflowPage,
        pageId: "EditWorkflow",
        shouldRender: fgaOrAdmin("can_view_workflows_page"),
      }),
      webhooks: new Route({
        matches: ["/webhooks"],
        Page: WorkflowsWebhooksPage,
        pageId: "WorkflowsWebhooks",
        shouldRender: fgaOrAdmin("can_view_workflows_page"),
      }),
    },
  }),
};
